import React from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  TextField,
  Button,
  IconButton,
  Slider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import useEmailEditorStore from '../../utilities/emailEditorStore';
import TypographyProps from './common/TypographyProps';
import BlockProps from './common/BlockProps';

function ListItemsBlockProperties({ blockId }) {
  const { getBlockById, updateBlockProperties } = useEmailEditorStore();
  
  const block = getBlockById(blockId);
  
  if (!block) {
    return <Typography>Block not found</Typography>;
  }
  
  const properties = block.properties || {};
  const items = properties.items || [];

  const handlePropertyChange = (propertyName, value) => {
    updateBlockProperties(blockId, { [propertyName]: value });
  };

  const handleAddItem = () => {
    const newItems = [...items, `List item ${items.length + 1}`];
    handlePropertyChange('items', newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    handlePropertyChange('items', newItems);
  };

  const handleUpdateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    handlePropertyChange('items', newItems);
  };

  const handleMoveItem = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    handlePropertyChange('items', newItems);
  };

  const listStyleOptions = properties.listType === 'ul' 
    ? [
        { value: 'disc', label: 'Disc (●)' },
        { value: 'circle', label: 'Circle (○)' },
        { value: 'square', label: 'Square (■)' },
        { value: 'none', label: 'None' }
      ]
    : [
        { value: 'decimal', label: 'Numbers (1, 2, 3)' },
        { value: 'lower-alpha', label: 'Lowercase (a, b, c)' },
        { value: 'upper-alpha', label: 'Uppercase (A, B, C)' },
        { value: 'lower-roman', label: 'Roman lowercase (i, ii, iii)' },
        { value: 'upper-roman', label: 'Roman uppercase (I, II, III)' },
        { value: 'none', label: 'None' }
      ];

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>List Settings</Typography>
      
      {/* List Type */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>List Type</InputLabel>
        <Select
          value={properties.listType || 'ul'}
          label="List Type"
          onChange={(e) => {
            handlePropertyChange('listType', e.target.value);
            // Reset list style type when changing list type
            const defaultStyle = e.target.value === 'ul' ? 'disc' : 'decimal';
            handlePropertyChange('listStyleType', defaultStyle);
          }}
        >
          <MenuItem value="ul">Unordered List (Bullets)</MenuItem>
          <MenuItem value="ol">Ordered List (Numbers)</MenuItem>
        </Select>
      </FormControl>

      {/* List Style Type */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>List Style</InputLabel>
        <Select
          value={properties.listStyleType || (properties.listType === 'ul' ? 'disc' : 'decimal')}
          label="List Style"
          onChange={(e) => handlePropertyChange('listStyleType', e.target.value)}
        >
          {listStyleOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Marker Color */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Marker Color</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type="color"
            value={properties.markerColor || '#000000'}
            onChange={(e) => handlePropertyChange('markerColor', e.target.value)}
            sx={{ width: 60 }}
          />
          <TextField
            value={properties.markerColor || '#000000'}
            onChange={(e) => handlePropertyChange('markerColor', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>
      </Box>

      {/* Item Spacing */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Item Spacing: {properties.itemSpacing || 8}px
        </Typography>
        <Slider
          value={properties.itemSpacing || 8}
          onChange={(e, value) => handlePropertyChange('itemSpacing', value)}
          min={0}
          max={30}
          step={1}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      {/* List Items */}
      <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>List Items</Typography>
      <Box sx={{ mb: 2 }}>
        {items.map((item, index) => (
          <Box key={index} sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              multiline
              size="small"
              value={item}
              onChange={(e) => handleUpdateItem(index, e.target.value)}
              placeholder={`Item ${index + 1}`}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleMoveItem(index, 'up')}
                disabled={index === 0}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleMoveItem(index, 'down')}
                disabled={index === items.length - 1}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveItem(index)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 1 }}
        >
          Add Item
        </Button>
      </Box>

      {/* Typography Properties */}
      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Typography</Typography>
      <TypographyProps properties={properties} onPropertyChange={handlePropertyChange} />
      
      {/* Block Properties */}
      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Block Settings</Typography>
      <BlockProps properties={properties} onPropertyChange={handlePropertyChange} />
    </>
  );
}

export default ListItemsBlockProperties;
